import {
	DiscountCoverageEnum,
	DiscountMeasuringPeriodOptionLabels,
	DiscountMethodOptionLabels,
	DiscountUnitOptions,
	FreeOrDiscountedInitiationOptionLabels,
	FreeOrDiscountedInitiationScopeOptionLabels,
	IncreasePatternOptions,
	ModelTypeOptions,
	OutcomeMeasureTypeLabels,
	TimeUnitOption,
	VolumeBaseOptionLabels,
	VolumeMeasureOptionLabels,
} from "../../../../models/simulator/options";
import { IEvaluationThresholdInput, IPatientShareInput, IScenarioInput, ITreatmentDurationInput } from "./../inputs";
import { IAdditionalDiscount, IAdditionalDiscountNotIncluded, IFinancialModelInput, SimulationContext } from "./../simulation-context";
import {
	calculateDurationInMonths,
	ensureDate,
	getDurationInMonths,
	getMonthsUntilReset,
	getMonthToReset,
	rangesOverlap,
} from "./../utils";
import { IMonthlyPatientSplit, getEnrolledPatients } from "./../patient-service";
import { maxBy } from "../../../../utils/max-by";
import { addMonths, addYears, differenceInDays, differenceInMonths, isAfter, startOfDay, subDays } from "date-fns";
import {
	IDiscount,
	IFinancialEntry,
	IGetDiscountArgs,
	IGetFinancialDataArgs,
	IGetFreeOrDiscountedInitiationDiscountArgs,
	IGetOutcomeBasedDiscountArgs,
	IGetScenariosForCurrentDate,
	IGetVolumeBasedDiscountArgs,
	IScenariosDiscount,
} from "./interfaces";
import { toFixedNumber } from "../../details/output/functions";
import { patternAddDuration } from "../treatment-service";
import currencyFormat from "apps/platform-client/client/src/utils/currency-format";

export const biggestPossibleValue = 999999999999999;

export const getFinancialData = (args: IGetFinancialDataArgs): IFinancialEntry => {
	const {
		numberOfPatients,
		treatmentPrice,
		patientSplitId,
		packName,
		packAdministrationDate,
		quantity,
		distributedPack,
		cohortStartDate,
		treatmentDuration,
		receivingTreatment,
		currentDate,
		currentMonth,
		sectionId,
		isSingleDoseTreatment,
		isFirstDistributionInAMonth,
		weightGroupName,
		context,
	} = args;
	const { productPacks, models } = context.getFinancialServiceInput(sectionId);

	const discounts = models.map((model) => {
		const discount = getDiscount({
			treatmentPrice,
			numberOfPatients,
			distributedPack,
			cohortStartDate,
			treatmentDuration,
			packName,
			packAdministrationDate,
			quantity,
			patientSplitId,
			receivingTreatment,
			currentDate,
			currentMonth,
			context,
			sectionId,
			productPacks,
			model,
			isSingleDoseTreatment,
			isFirstDistributionInAMonth,
		});

		return {
			discount: 0,
			scenariosDiscount: discount.scenarios,
			modelType: model.type!,
			weightGroupName: weightGroupName,
		};
	});

	return {
		discounts,
	};
};

export const getDiscount = (args: IGetDiscountArgs): IDiscount => {
	const { model, treatmentPrice, numberOfPatients, quantity, isSingleDoseTreatment, ...other } = args;
	const validity = model.validity;

	if (validity && validity.startDate && validity.endDate) {
		const cohortStartDate = other.cohortStartDate;
		const isCohortStartOutsideValidity =
			isAfter(ensureDate(cohortStartDate), ensureDate(validity.endDate)) ||
			isAfter(ensureDate(validity.startDate), ensureDate(cohortStartDate));

		const isAdmWithinValidity = checkAdministrationInModelValidity(model, args.distributedPack.administrationDate);
		if (isCohortStartOutsideValidity || !isAdmWithinValidity) {
			return {
				scenarios: [],
			};
		}
	}

	switch (model.type) {
		case ModelTypeOptions.INDICATION_BASED:
			return getIndicationDiscount(model.scenarios, args.context);
		case ModelTypeOptions.FIXED_FEE_DISCOUNT:
			return getFixedFeeDiscount(model.scenarios, treatmentPrice, args.context);
		case ModelTypeOptions.OUTCOME_BASED:
			return getOutcomeBasedDiscount({ model, isSingleDoseTreatment, ...other });
		case ModelTypeOptions.INGREDIENT_BASED_DISCOUNT:
		case ModelTypeOptions.BUDGET_CAP:
		case ModelTypeOptions.PRICE_DOSE_CAP:
		case ModelTypeOptions.UTILIZATION_CAP:
			return getVolumeBasedDiscount({ model, treatmentPrice, numberOfPatients, ...other });
		case ModelTypeOptions.FREE_OR_DISCOUNTED_INITIATION:
			return getFreeOrDiscountedInitiationDiscount({
				model,
				treatmentPrice,
				quantity,
				numberOfPatients,
				...other,
			});
		default:
			return {
				scenarios: [],
			};
	}
};

const checkAdministrationInModelValidity = (model: IFinancialModelInput, packDistributionDate: Date): boolean => {
	const { validity } = model;
	const defaultDiscountCoverage =
		model.type === ModelTypeOptions.OUTCOME_BASED ? DiscountCoverageEnum.CONTINUOUS : DiscountCoverageEnum.STRICT;
	const discountCoverage = validity?.discountCoverage || defaultDiscountCoverage;

	if (!validity?.startDate || !validity?.endDate || discountCoverage !== DiscountCoverageEnum.STRICT) {
		return true;
	}

	const distribution = ensureDate(packDistributionDate);
	const start = ensureDate(validity.startDate);
	const end = ensureDate(validity.endDate);

	return distribution >= start && distribution <= end;
};

export const getIndicationDiscount = (scenarios: Array<IScenarioInput>, context: SimulationContext): IDiscount => {
	return {
		scenarios: [
			{
				id: scenarios[0].id,
				discountName: `${scenarios[0].condition.value}`,
				discount: +scenarios[0].action.value,
				isNested: context.getIsNested(scenarios[0]),
			},
		],
	};
};

export const getFixedFeeDiscount = (scenarios: Array<IScenarioInput>, treatmentPrice: number, context: SimulationContext): IDiscount => {
	const eachScenarioDiscount = scenarios.map((scenario) => {
		const discount =
			scenario.action.unit === DiscountUnitOptions.PERCENTAGE
				? +scenario.action.value
				: (scenario.action.value * 100) / treatmentPrice;

		return {
			id: scenario.id,
			discountName: `${scenario.action.value}`,
			discount,
			isNested: context.getIsNested(scenario),
		};
	});

	return {
		scenarios: eachScenarioDiscount,
	};
};

export const getAggregationPerPack = (
	volumeMeasure: VolumeMeasureOptionLabels,
	aggregationOptions: Array<{
		volumeMeasure: VolumeMeasureOptionLabels;
		aggregationPerPack: number;
	}>
) => {
	const aggregationOption = aggregationOptions.find((option) => option.volumeMeasure === volumeMeasure);
	return aggregationOption?.aggregationPerPack || 0;
};

export const getFreeOrDiscountedAggregationPerPack = (
	volumeMeasure: FreeOrDiscountedInitiationOptionLabels,
	aggregationOptions: Array<{
		volumeMeasure: FreeOrDiscountedInitiationOptionLabels;
		aggregationPerPack: number;
	}>
) => {
	const aggregationOption = aggregationOptions.find((option) => option.volumeMeasure === volumeMeasure);
	return aggregationOption?.aggregationPerPack || 0;
};

export const getScenarioNameForRanges = (scenario: IScenarioInput) => {
	const startValue = currencyFormat(scenario.action.startValue, 1);
	if (scenario.action.endValue) {
		return `${startValue} - ${currencyFormat(scenario.action.endValue, 1)}`;
	}
	return `≥ ${startValue}`;
};

function recalculateDiscountAmount(discountAmount: any, originalCount: any, actualQuantity: any) {
	if (originalCount === 0 || actualQuantity === 0) {
		return 0;
	}

	const adjustedDiscountAmount = (discountAmount / originalCount) * actualQuantity;

	return adjustedDiscountAmount;
}

const getScenariosForCurrentDate = ({ scenarios, currentDate, isTreatmentYear, monthsSinceStart }: IGetScenariosForCurrentDate) => {
	const filteredScenarios = scenarios.filter((scenario) => {
		const validity = scenario.validity;

		if (isTreatmentYear && validity && validity.year) {
			const start = (validity.year - 1) * 12 + 1; // first month of that year
			const end = validity.year * 12; // last month of that year

			return monthsSinceStart >= start && monthsSinceStart <= end;
		}
		if (validity && validity.startDate && validity.endDate) {
			const isOutsideValidityRange =
				isAfter(ensureDate(currentDate), ensureDate(validity.endDate)) ||
				isAfter(startOfDay(ensureDate(validity.startDate)), ensureDate(currentDate));

			return !isOutsideValidityRange;
		} else {
			return true;
		}
	});

	return filteredScenarios;
};

export const getVolumeBasedDiscount = (args: IGetVolumeBasedDiscountArgs): IDiscount => {
	const {
		numberOfPatients,
		currentDate,
		currentMonth,
		sectionId,
		treatmentPrice,
		receivingTreatment,
		context,
		patientSplitId,
		distributedPack,
		model,
		cohortStartDate,
		isFirstDistributionInAMonth,
	} = args;
	const { packName, quantity } = distributedPack;
	const { type, volumeScope, volumeMeasure, scenarios, discountMethod, discountMeasuringCalendarMonth, discountMeasuringPeriod } = model;

	const isVolumeScopePatient = volumeScope === VolumeBaseOptionLabels.PATIENT_LEVEL;
	const isVolumeScopeSimulation = volumeScope === VolumeBaseOptionLabels.SIMULATION_LEVEL;
	const isPatientVolumeMeasure = volumeMeasure === VolumeMeasureOptionLabels.PATIENTS;
	const isRetroactively = DiscountMethodOptionLabels.RETROACTIVELY === discountMethod;
	const isTreatmentYear = discountMeasuringPeriod === DiscountMeasuringPeriodOptionLabels.TREATMENT_YEAR;

	if (!volumeMeasure || !(isVolumeScopePatient || isVolumeScopeSimulation) || quantity === 0) {
		return {
			scenarios: [],
		};
	}

	if (isVolumeScopePatient && isTreatmentYear) {
		const monthsSinceStart = calculateDurationInMonths(cohortStartDate, currentDate);
		const lastReset = context.getTreatmentYearResetPerCohort(sectionId, patientSplitId);
		const needsReset = requireReset({ monthsSinceStart, lastReset, isFirstDistributionInAMonth });
		if (needsReset) {
			context.updateTreatmentYearResetPerCohort(sectionId, patientSplitId, monthsSinceStart);
			context.removeVolumeBasedAggregationValue(sectionId, patientSplitId);
		}
	}
	const simulationStartDate = context.getSimulationStartDate();
	const scenariosDiscount: Array<IScenariosDiscount> = [];
	const aggregationTotal = context.getVolumeBasedTotalAggregationValue();
	const aggregationFromContext = context.getVolumeBasedAggregationValueForCohort(sectionId, patientSplitId)?.value;
	const aggregation = aggregationFromContext ? aggregationFromContext : 0;
	const aggregationOptions = [
		{
			volumeMeasure: VolumeMeasureOptionLabels.TREATMENT_COST,
			aggregationPerPack: treatmentPrice,
		},
		{
			volumeMeasure: VolumeMeasureOptionLabels.ACTIVE_INGREDIENT,
			aggregationPerPack: Number(packName?.split(" ")[0]),
		},
		{
			volumeMeasure: VolumeMeasureOptionLabels.QUANTITY,
			aggregationPerPack: 1,
		},
		{
			volumeMeasure: VolumeMeasureOptionLabels.PATIENTS,
			aggregationPerPack: 1,
		},
	];
	const aggregationPerPack = getAggregationPerPack(volumeMeasure, aggregationOptions);

	const getAgregationFinal = () => {
		if (isPatientVolumeMeasure) return receivingTreatment === 1 ? numberOfPatients : 0;
		return isVolumeScopePatient ? aggregationPerPack * (quantity / numberOfPatients) : aggregationPerPack * quantity;
	};

	const aggregationFinal = getAgregationFinal();
	const monthToReset = getMonthToReset(discountMeasuringPeriod, discountMeasuringCalendarMonth);
	const aggregationStart = isVolumeScopePatient ? aggregation : aggregationTotal;
	const aggregationEnd = (isVolumeScopePatient ? aggregation : aggregationTotal) + aggregationFinal;

	const indicationsPatientData = context.getPatientServiceInput(sectionId);
	const allPacks = aggregationFinal / aggregationPerPack || 1;
	const lastMonth = context.getSectionLastMonth(sectionId);
	const monthsUntilReset = getMonthsUntilReset(simulationStartDate, discountMeasuringPeriod, discountMeasuringCalendarMonth);
	let cohortAggregationValue = 0;
	if (isVolumeScopePatient) {
		cohortAggregationValue = aggregationFinal;
		context.updateVolumeBasedAggregationValue(sectionId, patientSplitId, cohortAggregationValue);
	} else {
		cohortAggregationValue = aggregationEnd;
		context.updateVolumeBasedAggregationValue(sectionId, patientSplitId, cohortAggregationValue);
	}

	// Only simulation-level (contract) volume should advance the global total used as aggregationTotal for other
	// SIMULATION_LEVEL models. Patient-level / nested PV must not pollute it — otherwise parallel contract PV tiers
	// see inflated cumulative volume after indication-based nested models run (e.g. SI-1489 vs SI-1490).
	if (isVolumeScopeSimulation) {
		context.addVolumeBasedTotalAggregationValue(aggregationFinal);
	}

	const currentScenarios = getScenariosForCurrentDate({
		scenarios,
		currentDate,
		isTreatmentYear,
		monthsSinceStart: receivingTreatment,
	});
	const usedScenarios = currentScenarios.filter((scenario) => {
		const { startValue, endValue } = scenario.action;

		const finalEndValue = endValue || biggestPossibleValue;

		return startValue && finalEndValue && rangesOverlap(startValue, finalEndValue, aggregationStart, aggregationEnd);
	});

	const totalPatientsCurrentMonth = (): number => {
		let totalMonthlyPatients = 0;

		for (let month = 0; month <= currentMonth; month++) {
			if (month === 0 && currentMonth >= monthsUntilReset) {
				month = monthsUntilReset + 12 * Math.floor((currentMonth - monthsUntilReset) / 12);
			}
			const revisitedMonth = addMonths(ensureDate(simulationStartDate), month);
			const isMonthWithinValidityPeriod = checkAdministrationInModelValidity(model, revisitedMonth);

			for (let i = 0; i < indicationsPatientData.length; i++) {
				const monthlyPatientsForIndication = isMonthWithinValidityPeriod
					? getEnrolledPatients(indicationsPatientData[i].patientEnrollment, month)
					: 0;
				totalMonthlyPatients = totalMonthlyPatients + monthlyPatientsForIndication;
			}
		}
		return totalMonthlyPatients;
	};

	const totalPatientsCurrentYear = (): number => {
		let total = 0;
		const startOfYear =
			currentMonth >= monthsUntilReset ? monthsUntilReset + 12 * Math.floor((currentMonth - monthsUntilReset) / 12) : 0;
		const endOfYear =
			currentMonth >= monthsUntilReset
				? monthsUntilReset + 12 * Math.floor((currentMonth - monthsUntilReset) / 12) + 12
				: monthsUntilReset;

		for (let month = startOfYear; month < endOfYear && month < lastMonth; month++) {
			const revisitedMonth = addMonths(ensureDate(simulationStartDate), month);
			const isMonthWithinValidityPeriod = checkAdministrationInModelValidity(model, revisitedMonth);
			for (let i = 0; i < indicationsPatientData.length; i++) {
				const monthlyPatientsForIndication = isMonthWithinValidityPeriod
					? getEnrolledPatients(indicationsPatientData[i].patientEnrollment, month)
					: 0;
				total += monthlyPatientsForIndication;
			}
		}
		return total;
	};
	const totalMonthlyPatients = isRetroactively ? totalPatientsCurrentYear() : totalPatientsCurrentMonth();
	const maxPatientScenario = scenarios.find((scenario) => {
		return (
			(scenario.action.endValue !== undefined &&
				scenario.action.startValue <= totalMonthlyPatients &&
				scenario.action.endValue >= totalMonthlyPatients) ||
			(scenario.action.endValue === undefined && scenario.action.startValue <= totalMonthlyPatients)
		);
	});

	if (isPatientVolumeMeasure && maxPatientScenario && usedScenarios.length === 0) {
		usedScenarios.push(maxPatientScenario);
	}

	usedScenarios.reduce(
		(result, scenario, index) => {
			const previousResult = { ...result };
			const { startValue, endValue } = scenario.action;
			const discountRate = Number(scenario.action.discountRate);
			if (!startValue || discountRate === undefined || result.count > allPacks) {
				return result;
			}

			if (isPatientVolumeMeasure && usedScenarios[index + 1]) {
				return { discount: 0, count: 0, discountNotIncluded: 0, id: scenario.id };
			}

			const previousScenarioEndValue = usedScenarios[index - 1]?.action.endValue
				? usedScenarios[index - 1].action.endValue! + 1
				: isVolumeScopePatient
				? aggregation + 1
				: aggregationTotal + 1;
			const nextScenarioStartValue = usedScenarios[index + 1]?.action.startValue
				? usedScenarios[index + 1].action.startValue! - 1
				: aggregationEnd;

			const reachedStartValue = previousScenarioEndValue;
			const reachedEndValue = Math.min(nextScenarioStartValue, endValue || biggestPossibleValue);

			if (isPatientVolumeMeasure) {
				result.discount = discountRate;
				result.count = numberOfPatients;
			} else {
				for (let i = reachedStartValue; i <= reachedEndValue; ) {
					const nextScenarioStart = usedScenarios[index + 1]?.action.startValue;
					const coveredByNextScenario = !!nextScenarioStart && i + aggregationPerPack > nextScenarioStart;
					if (
						(i >= startValue && i <= (endValue || biggestPossibleValue) && !coveredByNextScenario) ||
						(i + aggregationPerPack - 1 >= startValue && !coveredByNextScenario)
					) {
						result.discount += discountRate;
						result.count += 1;
					}
					i += aggregationPerPack;
				}

				//   The loop above handles only whole numbers. This additional check adds missing fraction part to the discount
				if (!isVolumeScopePatient && volumeMeasure === VolumeMeasureOptionLabels.QUANTITY) {
					const remainingFraction = quantity - result.count >= 1 ? 0 : toFixedNumber(quantity - result.count);
					if (remainingFraction > 0) {
						const partialDiscount = discountRate * remainingFraction;
						result.discount += partialDiscount;
					}
				}
			}

			if (
				startValue > previousScenarioEndValue &&
				index > 0 &&
				(startValue - previousScenarioEndValue - 1) / aggregationPerPack > 0 &&
				(!isPatientVolumeMeasure || (isPatientVolumeMeasure && !isRetroactively))
			) {
				result.discountNotIncluded += (startValue - previousScenarioEndValue - 1) / aggregationPerPack;
			}

			if (endValue && index === usedScenarios.length - 1 && aggregationEnd > endValue) {
				result.discountNotIncluded += (aggregationEnd - endValue) / aggregationPerPack;
			}

			const scenarioName = getScenarioNameForRanges(scenario);

			if (result.discountNotIncluded > 0) {
				const discountNotIncludedCount = isVolumeScopeSimulation
					? result.discountNotIncluded
					: result.discountNotIncluded * numberOfPatients;
				context.addDiscountNotIncludedCount({
					sectionId,
					patientSplitId,
					month: currentMonth,
					count: discountNotIncludedCount,
					packName,
					scenarioId: scenario.id,
					discountName: scenarioName,
				});
			}

			if (isRetroactively) {
				if (isPatientVolumeMeasure && scenario.action.discountRate) {
					if (maxPatientScenario && maxPatientScenario.action.discountRate) {
						const discount = maxPatientScenario.action.discountRate;
						const endOfDiscountPeriod = isVolumeScopeSimulation ? 0 : currentMonth - receivingTreatment + 1;
						const discountPatientSplitId = isVolumeScopeSimulation ? "" : patientSplitId;

						for (let month = currentMonth; month >= endOfDiscountPeriod; month--) {
							const monthDifference = -1 * Math.abs(currentMonth - month);
							const revisitedMonth = addMonths(currentDate, monthDifference);
							const isRevisitedMonthWithinValidityPeriod = checkAdministrationInModelValidity(model, revisitedMonth);

							if (
								!isRevisitedMonthWithinValidityPeriod ||
								(monthToReset >= 0 && revisitedMonth.getMonth() == monthToReset && monthDifference !== 0)
							)
								break;

							context.addDiscount({
								id: maxPatientScenario.id,
								sectionId,
								patientSplitId: discountPatientSplitId,
								discount,
								month,
								packName: "",
								modelType: type,
								modelValidity: model.validity,
								discountName: getScenarioNameForRanges(maxPatientScenario),
								conditionType: volumeMeasure,
								discountCount: result.count * (isVolumeScopePatient ? numberOfPatients : 1),
							});
						}
					}
				} else {
					const retroactiveDiscount = usedScenarios[usedScenarios.length - 1];
					if (
						retroactiveDiscount.action.discountRate !== undefined &&
						(usedScenarios.length > 1 || aggregationStart <= retroactiveDiscount.action.startValue)
					) {
						const discount = retroactiveDiscount.action.discountRate;
						const endOfDiscountPeriod = isVolumeScopeSimulation ? 0 : currentMonth - receivingTreatment + 1;
						const discountPatientSplitId = isVolumeScopeSimulation ? "" : patientSplitId;

						for (let month = currentMonth; month >= endOfDiscountPeriod; month--) {
							const monthDifference = -1 * Math.abs(currentMonth - month);
							const revisitedMonth = addMonths(currentDate, monthDifference);
							if (discountMeasuringPeriod !== DiscountMeasuringPeriodOptionLabels.TREATMENT_YEAR) {
								if (monthToReset >= 0 && revisitedMonth.getMonth() === monthToReset && monthDifference !== 0) break;
							} else {
								if (
									(receivingTreatment - (currentMonth - month)) % 12 === 0 &&
									!(receivingTreatment % 12 === 0 && month === currentMonth)
								)
									break;
							}

							context.addDiscount({
								id: retroactiveDiscount.id,
								sectionId,
								patientSplitId: discountPatientSplitId,
								discount,
								month,
								packName: "",
								modelType: type,
								modelValidity: model.validity,
								discountName: getScenarioNameForRanges(retroactiveDiscount),
								conditionType: volumeMeasure,
								discountCount: result.count * (isVolumeScopePatient ? numberOfPatients : 1),
							});

							context.removeDiscountsNotIncluded(sectionId, discountPatientSplitId, month);
						}
					}
				}
			}

			if (scenario.action.discountRate && (!isPatientVolumeMeasure || (isPatientVolumeMeasure && !isRetroactively))) {
				if (isPatientVolumeMeasure) {
					if (maxPatientScenario) {
						const scenarioName = getScenarioNameForRanges(maxPatientScenario);

						const thisScenarioDiscount = {
							...result,
							id: maxPatientScenario.id,
							discountName: scenarioName,
							conditionType: volumeMeasure,
							discount: maxPatientScenario?.action.discountRate || 0,
							isNested: context.getIsNested(maxPatientScenario),
							validityYear: maxPatientScenario.validity?.year,
						};

						scenariosDiscount.push(thisScenarioDiscount);
					}
				} else {
					const scenarioName = getScenarioNameForRanges(scenario);

					const discountToApply =
						isVolumeScopeSimulation && !isRetroactively && result.count > quantity
							? recalculateDiscountAmount(result.discount, result.count, quantity)
							: result.discount;

					const finalDiscount = (discountToApply - previousResult.discount) / allPacks;

					function getDiscountPerPack(numberOfPacks: number, originalDiscount: number, eligiblePacks: number) {
						if (isVolumeScopeSimulation || eligiblePacks > numberOfPacks || discountRate == finalDiscount) {
							return undefined;
						}

						const discountPerPack = [];

						for (let i = 1; i <= numberOfPacks; i++) {
							discountPerPack.push(i <= eligiblePacks ? originalDiscount : 0);
						}

						return discountPerPack;
					}

					const discountPerPack = getDiscountPerPack(allPacks, discountRate, result.count);

					const thisScenarioDiscount = {
						...result,
						id: scenario.id,
						discountName: scenarioName,
						conditionType: volumeMeasure,
						discount: finalDiscount,
						isNested: context.getIsNested(scenario),
						originalDiscount: discountRate,
						splittedPackCount: isVolumeScopeSimulation ? 1 : result.count,
						//  provides discount for each pack separately instead of average number
						discountPerPack: discountPerPack,
						packsPerPatient: allPacks,
						validityYear: scenario.validity?.year,
					};

					scenariosDiscount.push(thisScenarioDiscount);
				}
			}

			return result;
		},
		{ discount: 0, count: 0, discountNotIncluded: 0 }
	);

	return {
		scenarios: scenariosDiscount,
	};
};

export const getFreeOrDiscountedInitiationDiscount = (args: IGetFreeOrDiscountedInitiationDiscountArgs): IDiscount => {
	if (args?.model?.freeOrDiscountedMeasure === FreeOrDiscountedInitiationOptionLabels.DAYS_OF_TREATMENT)
		return getFreeOrDiscountedInitiationDiscountDays(args);
	else return getFreeOrDiscountedInitiationDiscountVolume(args);
};

export const getFreeOrDiscountedInitiationDiscountVolume = (args: IGetFreeOrDiscountedInitiationDiscountArgs): IDiscount => {
	const {
		numberOfPatients,
		currentMonth,
		currentDate,
		sectionId,
		packName,
		context,
		patientSplitId,
		quantity,
		model,
		cohortStartDate,
		isFirstDistributionInAMonth,
	} = args;
	const { freeOrDiscountedScope, freeOrDiscountedMeasure, scenarios, discountMeasuringPeriod } = model;
	const isVolumeScopePatient = freeOrDiscountedScope === FreeOrDiscountedInitiationScopeOptionLabels.PATIENT_LEVEL;

	if (!freeOrDiscountedMeasure || !isVolumeScopePatient) {
		return {
			scenarios: [],
		};
	}

	if (isVolumeScopePatient && discountMeasuringPeriod == DiscountMeasuringPeriodOptionLabels.TREATMENT_YEAR) {
		const monthsSinceStart = calculateDurationInMonths(cohortStartDate, currentDate);
		const lastReset = context.getTreatmentYearResetPerCohort(sectionId, patientSplitId);
		const needsReset = requireReset({ monthsSinceStart, lastReset, isFirstDistributionInAMonth });

		if (needsReset) {
			context.updateTreatmentYearResetPerCohort(sectionId, patientSplitId, monthsSinceStart);
			context.removeFreeOrDiscountedAggregationValue(sectionId, patientSplitId);
		}
	}

	const scenariosDiscount: Array<IScenariosDiscount> = [];
	const aggregationTotal = context.getFreeOrDiscountedTotalAggregationValue();
	const aggregationFromContext = context.getFreeOrDiscountedAggregationValueForCohort(sectionId, patientSplitId)?.value;

	const aggregation = aggregationFromContext ? aggregationFromContext : 0;
	const aggregationOptions = [
		{
			volumeMeasure: FreeOrDiscountedInitiationOptionLabels.ACTIVE_INGREDIENT_QUANTITY,
			aggregationPerPack: Number(packName?.split(" ")[0]),
		},
		{
			volumeMeasure: FreeOrDiscountedInitiationOptionLabels.PACK_QUANTITY,
			aggregationPerPack: 1,
		},
	];
	const aggregationPerPack = getFreeOrDiscountedAggregationPerPack(freeOrDiscountedMeasure, aggregationOptions);

	const getAgregationFinal = () => {
		return isVolumeScopePatient ? aggregationPerPack * (quantity / numberOfPatients) : aggregationPerPack * quantity;
	};

	const aggregationFinal = getAgregationFinal();
	const aggregationStart = isVolumeScopePatient ? aggregation : aggregationTotal + 1;
	const aggregationEnd = (isVolumeScopePatient ? aggregation : aggregationTotal) + aggregationFinal;
	const allPacks = aggregationFinal / aggregationPerPack || 1;

	let cohortAggregationValue = 0;

	if (isVolumeScopePatient) {
		cohortAggregationValue = aggregationFinal;
		context.updateFreeOrDiscountedAggregationValue(sectionId, patientSplitId, cohortAggregationValue);
	} else {
		cohortAggregationValue = aggregationEnd;
		context.updateFreeOrDiscountedAggregationValue(sectionId, patientSplitId, cohortAggregationValue);
	}

	context.addFreeOrDiscountedTotalAggregationValue(aggregationFinal);

	const usedScenarios = scenarios.filter((scenario) => {
		const { startValue, endValue } = scenario.action;
		const finalEndValue = endValue || biggestPossibleValue;

		return startValue && finalEndValue && rangesOverlap(startValue, finalEndValue, aggregationStart, aggregationEnd);
	});

	usedScenarios.reduce(
		(result, scenario, index) => {
			const previousResult = { ...result };
			const { startValue, endValue, discountRate } = scenario.action;
			if (!startValue || discountRate === undefined || result.count > allPacks) {
				return result;
			}

			const previousScenarioEndValue = usedScenarios[index - 1]?.action.endValue
				? usedScenarios[index - 1].action.endValue! + 1
				: isVolumeScopePatient
				? aggregation + 1
				: aggregationTotal + 1;
			const nextScenarioStartValue = usedScenarios[index + 1]?.action.startValue
				? usedScenarios[index + 1].action.startValue! - 1
				: aggregationEnd;

			const reachedStartValue = previousScenarioEndValue;
			const reachedEndValue = Math.min(nextScenarioStartValue, endValue || biggestPossibleValue);

			for (let i = reachedStartValue; i <= reachedEndValue; ) {
				const coveredByNextScenario =
					!!usedScenarios[index + 1]?.action.startValue && i + aggregationPerPack > usedScenarios[index + 1]?.action.startValue;
				if (
					(i >= startValue && i <= (endValue || biggestPossibleValue) && !coveredByNextScenario) ||
					(i + aggregationPerPack - 1 >= startValue && !coveredByNextScenario)
				) {
					result.discount += discountRate;
					result.count += 1;
				}
				i += aggregationPerPack;
			}

			if (
				startValue > previousScenarioEndValue &&
				index > 0 &&
				(startValue - previousScenarioEndValue - 1) / aggregationPerPack > 0
			) {
				result.discountNotIncluded += (startValue - previousScenarioEndValue - 1) / aggregationPerPack;
			}

			if (endValue && index === usedScenarios.length - 1 && aggregationEnd > endValue) {
				result.discountNotIncluded += (aggregationEnd - endValue) / aggregationPerPack;
			}

			const scenarioName = getScenarioNameForRanges(scenario);

			if (result.discountNotIncluded > 0) {
				const discountNotIncludedCount = result.discountNotIncluded * numberOfPatients;
				context.addDiscountNotIncludedCount({
					sectionId,
					patientSplitId,
					month: currentMonth,
					count: discountNotIncludedCount,
					packName,
					scenarioId: scenario.id,
					discountName: scenarioName,
				});
			}

			if (scenario.action.discountRate) {
				const scenarioName = getScenarioNameForRanges(scenario);

				const thisScenarioDiscount = {
					...result,
					id: scenario.id,
					discountName: scenarioName,
					conditionType: freeOrDiscountedMeasure,
					discount: toFixedNumber((result.discount - previousResult.discount) / allPacks),
					isNested: context.getIsNested(scenario),
				};

				scenariosDiscount.push(thisScenarioDiscount);
			}
			return result;
		},
		{ discount: 0, count: 0, discountNotIncluded: 0 }
	);
	return {
		scenarios: scenariosDiscount,
	};
};

export const getFreeOrDiscountedInitiationDiscountDays = (args: IGetFreeOrDiscountedInitiationDiscountArgs): IDiscount => {
	const { context, model, cohortStartDate } = args;
	const { freeOrDiscountedScope, freeOrDiscountedMeasure, scenarios, discountMeasuringPeriod } = model;
	const isVolumeScopePatient = freeOrDiscountedScope === FreeOrDiscountedInitiationScopeOptionLabels.PATIENT_LEVEL;
	const isDaysOnTreatmentMeasure = freeOrDiscountedMeasure === FreeOrDiscountedInitiationOptionLabels.DAYS_OF_TREATMENT;

	if (!freeOrDiscountedMeasure || !isVolumeScopePatient || !isDaysOnTreatmentMeasure) {
		return {
			scenarios: [],
		};
	}

	const scenariosDiscount: Array<IScenariosDiscount> = [];

	scenarios.reduce(
		(result, scenario) => {
			const { startValue, endValue, discountRate } = scenario.action;
			if (!startValue || discountRate === undefined) {
				return result;
			}

			result.discount = discountRate;
			result.count = 1;

			let dayOfAdministration = Math.abs(differenceInDays(cohortStartDate, args.packAdministrationDate)) + 1;
			if (discountMeasuringPeriod == DiscountMeasuringPeriodOptionLabels.TREATMENT_YEAR) {
				const daysInYear = Math.abs(differenceInDays(cohortStartDate, addYears(cohortStartDate, 1))) + 1;
				dayOfAdministration = dayOfAdministration % daysInYear;
			}

			const canApplyScenario = dayOfAdministration >= startValue && dayOfAdministration <= (endValue || biggestPossibleValue);

			if (scenario.action.discountRate && canApplyScenario) {
				const scenarioName = getScenarioNameForRanges(scenario);
				const thisScenarioDiscount = {
					...result,
					id: scenario.id,
					discountName: scenarioName,
					conditionType: freeOrDiscountedMeasure,
					discount: scenario?.action.discountRate || 0,
					isNested: context.getIsNested(scenario),
				};

				scenariosDiscount.push(thisScenarioDiscount);
			}
			return result;
		},
		{ discount: 0, count: 0, discountNotIncluded: 0 }
	);
	return {
		scenarios: scenariosDiscount,
	};
};

export const willReceiveEvaluation = (
	isSingleDoseTreatment: boolean,
	treatmentDuration: ITreatmentDurationInput,
	cohortStartDate: Date,
	threshold: number,
	context: SimulationContext
) => {
	const simulationEndDate = new Date(context.getSimulationEndDate());
	const lastTreatmentDay =
		treatmentDuration.type === IncreasePatternOptions.NON_LINEAR || isSingleDoseTreatment
			? simulationEndDate
			: subDays(patternAddDuration[treatmentDuration.unit](cohortStartDate, treatmentDuration.duration), 1);

	const endOfTreatment = isAfter(new Date(lastTreatmentDay), simulationEndDate) ? simulationEndDate : lastTreatmentDay;
	const plannedEvaluation = addMonths(cohortStartDate, threshold - 1);
	const isEvaluated = isAfter(endOfTreatment, plannedEvaluation);

	return isEvaluated;
};

export const getOutcomeBasedDiscount = (args: IGetOutcomeBasedDiscountArgs): IDiscount => {
	const { patientSplitId, receivingTreatment, sectionId, context, currentMonth, cohortStartDate, treatmentDuration } = args;
	const { evaluationThresholds, evaluationPointThresholds, scenarios, type, outcomeMeasureType } = args.model;
	if (!outcomeMeasureType) {
		throw new Error("Missing outcome measure type!");
	}

	const scenariosByMeasureType = scenarios?.filter((scenario) => scenario.measureType === outcomeMeasureType);
	if (outcomeMeasureType === OutcomeMeasureTypeLabels.TIME_TO_TREATMENT_DISCONTINUATION) {
		if (!evaluationPointThresholds?.length) {
			throw new Error("Missing condition evaluation for outcome!");
		}

		const simulationEndDate = new Date(context.getSimulationEndDate());
		const discount = context
			.getSimulationCache()
			.select(
				getTimeToTreatmentDiscontinuationDiscount,
				evaluationPointThresholds,
				scenariosByMeasureType,
				treatmentDuration,
				receivingTreatment,
				cohortStartDate,
				simulationEndDate,
				context
			);

		discount.scenarios.forEach((scenario) =>
			context.addDiscount({
				id: scenario.id,
				sectionId,
				patientSplitId,
				packName: "",
				discount: scenario.discount,
				originalDiscount: scenario.originalDiscount,
				conditionType: scenario.conditionType,
				patientShare: scenario.patientShare,
				modelType: type,
				month: currentMonth,
				discountName: scenario.discountName,
			})
		);

		return discount;
	}

	if (!evaluationThresholds?.length || evaluationThresholds.some((threshold) => !threshold.patientShare))
		throw new Error("Missing patient share or condition evaluation for outcome!");

	const thresholdRanges = convertDurationsToRangesOtherType(evaluationThresholds, cohortStartDate, scenariosByMeasureType);

	const totalDiscount = thresholdRanges.reduce(
		(acc, range) => {
			if (receivingTreatment > range.end || receivingTreatment < range.start) {
				return acc;
			}

			const willBeEvaluated = willReceiveEvaluation(
				!!args.isSingleDoseTreatment,
				treatmentDuration,
				args.cohortStartDate,
				range.end,
				context
			);

			if (!willBeEvaluated) {
				return acc;
			}

			const discount = context
				.getSimulationCache()
				.select(calculateOutcomeBasedDiscount, range.patientShare, range.scenarios, context);

			discount.scenarios.forEach((scenario) =>
				context.addDiscount({
					id: scenario.id,
					sectionId,
					patientSplitId,
					packName: "",
					discount: scenario.discount,
					originalDiscount: scenario.originalDiscount,
					conditionType: scenario.conditionType,
					modelType: type,
					month: currentMonth,
					discountName: scenario.discountName,
					patientShare: scenario.patientShare,
				})
			);

			acc.scenarios.push(...discount.scenarios);
			return acc;
		},
		{ scenarios: [] as any }
	);

	return totalDiscount;
};

export function calculateOutcomeBasedDiscount(
	patientShares: Array<IPatientShareInput>,
	scenarios: Array<IScenarioInput>,
	context: SimulationContext
) {
	const eachScenarioDiscount: Array<IScenariosDiscount> = [];
	const totalShare = 100;
	scenarios.reduce((result, scenario) => {
		const patientShare = patientShares.find((patientShare) => patientShare.name === scenario.condition.value);
		const share = patientShare?.share || 0;
		const discount = (share * scenario.action.value) / totalShare;
		const isNested = context.getIsNested(scenario);
		eachScenarioDiscount.push({
			id: scenario.id,
			discountName: `${scenario.condition.value}`,
			conditionType: scenario.condition.type,
			isNested,
			originalDiscount: scenario.action.value,
			discount: discount,
			patientShare: share / totalShare,
		});
		return result + discount;
	}, 0);

	return {
		scenarios: eachScenarioDiscount,
	};
}

function convertDurationsToRangesTTD(evaluationPointThresholds: { scenario: IScenarioInput; unit: TimeUnitOption; duration: number }[]) {
	const sorted = [...evaluationPointThresholds].sort((a, b) => a.duration - b.duration);
	let currentStart = 1;

	return sorted.map((item) => {
		const end = item.duration;
		const range = { start: currentStart, end, scenario: item.scenario };
		currentStart = end + 1;

		return range;
	});
}

function findLastNumber(str: string) {
	const matches = str.match(/\d+/g);
	return matches ? +matches[matches.length - 1] : null;
}

function convertDurationsToRangesOtherType(
	evaluationThresholds: IEvaluationThresholdInput[],
	cohortStartDate: Date,
	scenarios: IScenarioInput[]
) {
	const scenarioWithDuration = scenarios.map((sc) => ({ ...sc, duration: findLastNumber(sc.condition.type) }));
	const sortedThresholds = [...evaluationThresholds].sort((a, b) => a.duration - b.duration);
	const monthlyRanges =
		evaluationThresholds[0].unit === TimeUnitOption.WEEK
			? sortedThresholds.map((threshold) => ({
					...threshold,
					duration: getDurationInMonths(threshold.duration, evaluationThresholds[0].unit, cohortStartDate),
					scenarios: scenarioWithDuration.filter((sc) => sc.duration === threshold.duration),
			  }))
			: sortedThresholds.map((threshold) => ({
					...threshold,
					scenarios: scenarioWithDuration.filter((sc) => sc.duration === threshold.duration),
			  }));

	let currentStart = 1;

	return monthlyRanges.map((item, index, arr) => {
		const end = item.duration;
		// check if there's another item later with the same duration
		const isDuplicate = arr.slice(index + 1).some((next) => next.duration === end);
		const range = {
			start: currentStart,
			end,
			patientShare: item.patientShare,
			scenarios: isDuplicate ? [] : item.scenarios,
		};

		//  move currentStart forward if next item has a larger duration
		const next = arr[index + 1];
		if (!next || next.duration > end) {
			currentStart = end + 1;
		} else {
			currentStart = end; // keep same start for duplicates
		}

		return range;
	});
}

const getTimeToTreatmentDiscontinuationDiscount = (
	evaluationPointThresholds: IEvaluationThresholdInput[],
	scenarios: IScenarioInput[],
	treatmentDuration: ITreatmentDurationInput,
	receivingTreatment: number,
	cohortStartDate: Date,
	simulationEndDate: Date,
	context: SimulationContext
) => {
	if (treatmentDuration.type === IncreasePatternOptions.LINEAR) {
		return { scenarios: [] };
	}

	const monthAvailableForTreatment = Math.min(
		Math.abs(differenceInMonths(cohortStartDate, simulationEndDate)),
		treatmentDuration.durationPercentages.length - 1
	);
	const unit = evaluationPointThresholds[0].unit;
	const pointsWithDiscountValue = evaluationPointThresholds.map((thr, index) => ({
		...thr,
		duration: getDurationInMonths(thr.duration, unit, cohortStartDate),
		scenario: scenarios[index],
	}));
	const sortedEvaluationRanges = convertDurationsToRangesTTD(pointsWithDiscountValue);
	const eachScenarioDiscount: Array<IScenariosDiscount> = [];
	sortedEvaluationRanges.map((evPoint) => {
		if (receivingTreatment > evPoint.end) {
			return;
		}

		const start = Math.max(evPoint.start, receivingTreatment) - 1;
		const end = Math.min(evPoint.end, monthAvailableForTreatment);
		const percentOfDiscontinuedPatients =
			start > end ? 0 : treatmentDuration.durationPercentages[start] - treatmentDuration.durationPercentages[end];
		const patientShare = percentOfDiscontinuedPatients / treatmentDuration.durationPercentages[receivingTreatment - 1];
		const discount =
			(percentOfDiscontinuedPatients * evPoint.scenario.action.value) / treatmentDuration.durationPercentages[receivingTreatment - 1];
		const isNested = context.getIsNested(evPoint.scenario);

		eachScenarioDiscount.push({
			id: evPoint.scenario.id,
			discountName: `${evPoint.scenario.condition.value}`,
			conditionType: evPoint.scenario.condition.type,
			isNested,
			originalDiscount: evPoint.scenario.action.value,
			discount: discount,
			patientShare,
		});
	});

	return { scenarios: eachScenarioDiscount };
};

/**
 * Volume "not included" counts are stored per patient/month; they must be applied only for the
 * same tier scenario as the discount line (`scenarioId` === `valueToApply.id`), otherwise a gap
 * in one nested PV (e.g. ingredient) could incorrectly scale another's discount (e.g. patient count).
 */
export const getNotIncludedDiscountQuantity = (
	discountsNotIncluded: Array<IAdditionalDiscountNotIncluded> | undefined,
	patientSplitId: string,
	scenarioId: string,
	packName: string | undefined
): number => {
	if (!discountsNotIncluded?.length) {
		return 0;
	}

	const pack = packName ?? "";
	const packMatches = (entry: IAdditionalDiscountNotIncluded) => !entry.packName || entry.packName === pack;
	const forPatient = discountsNotIncluded.filter((entry) => entry.patientSplitId === patientSplitId);

	return forPatient
		.filter((entry) => packMatches(entry) && entry.scenarioId === scenarioId)
		.reduce((sum, entry) => sum + entry.count, 0);
};

export const applyAdditionDiscount = (
	monthlySplit: IMonthlyPatientSplit,
	additionalDiscounts: Array<IAdditionalDiscount>,
	simulationStartDate: string,
	discountsNotIncluded?: Array<IAdditionalDiscountNotIncluded>
): IMonthlyPatientSplit => {
	return {
		currentMonth: monthlySplit.currentMonth,
		activePatients: monthlySplit.activePatients.map((activePatient) => {
			const additionalDiscount = additionalDiscounts?.filter(
				(additionalDiscount) => !additionalDiscount.patientSplitId || additionalDiscount.patientSplitId === activePatient.id
			);
			if (!additionalDiscount.length) {
				return activePatient;
			}

			const validity = additionalDiscounts[0].modelValidity;
			if (validity && validity.startDate && validity.endDate) {
				const cohortStartDate = addMonths(new Date(simulationStartDate), activePatient.treatmentStartMonth);
				const isCohortStartOutsideValidity =
					isAfter(ensureDate(cohortStartDate), ensureDate(validity.endDate)) ||
					isAfter(ensureDate(validity.startDate), ensureDate(cohortStartDate));

				if (isCohortStartOutsideValidity) {
					return activePatient;
				}
			}

			return {
				...activePatient,
				distributedPacks: activePatient.distributedPacks.map((distributedPack) => {
					const discountsToApply = additionalDiscount.filter(
						(discount) => !discount.packName || discount.packName === distributedPack.packName
					);

					return discountsToApply.length
						? {
								...distributedPack,
								discounts: distributedPack.discounts.map((discount) => {
									const valuesToApply = discountsToApply.filter(
										(discountToApply) => discountToApply.modelType === discount.modelType
									);

									if (discount.modelType === ModelTypeOptions.OUTCOME_BASED) {
										return {
											...discount,
											scenariosDiscount: valuesToApply.map((valueToApply) => ({
												id: valueToApply.id,
												discountName: valueToApply.discountName,
												conditionType: valueToApply.conditionType,
												discount: valueToApply.discount,
												originalDiscount: valueToApply.originalDiscount,
												patientShare: valueToApply.patientShare,
											})),
										};
									}

									const valueToApply = maxBy(valuesToApply, (valueToApply) => valueToApply.discount);

									if (valueToApply && (discount.scenariosDiscount.length || valuesToApply.length)) {
										const notIncludedQty = getNotIncludedDiscountQuantity(
											discountsNotIncluded,
											activePatient.id,
											valueToApply.id,
											distributedPack.packName
										);

										return {
											...discount,
											scenariosDiscount: [
												{
													id: valueToApply.id,
													discountName: valueToApply.discountName,
													conditionType: valueToApply.conditionType,
													discount: reduceNotIncludedDiscount(
														valueToApply.discount,
														distributedPack.quantity || 1,
														notIncludedQty
													),
													numberOfPacks: Math.abs(
														discount.scenariosDiscount.reduce(
															(result, scenarioDiscount) => (result += scenarioDiscount.numberOfPacks || 0),
															0
														) - (valueToApply.discountCount || 0)
													),
												},
											],
										};
									}

									return discount;
								}),
						  }
						: distributedPack;
				}),
			};
		}),
	};
};

export const reduceNotIncludedDiscount = (currentDiscount: number, quantity: number, numberOfNotIncludedQuantity: number) => {
	if (quantity <= 0) {
		return 0;
	}
	const includedQuantity = Math.max(0, quantity - numberOfNotIncludedQuantity);
	const scaled = (currentDiscount * includedQuantity) / quantity;

	// Avoid -0 when includedQuantity is 0 and currentDiscount < 0
	return includedQuantity === 0 ? 0 : scaled;
};

const requireReset = ({
	monthsSinceStart,
	lastReset,
	isFirstDistributionInAMonth,
}: {
	monthsSinceStart: number;
	lastReset: number | undefined;
	isFirstDistributionInAMonth: boolean;
}) => {
	const resetOnTime = monthsSinceStart % 12 === 0 && isFirstDistributionInAMonth;
	const delayedReset = monthsSinceStart > 12 && (!lastReset || monthsSinceStart - lastReset >= 12) && isFirstDistributionInAMonth;

	return resetOnTime || delayedReset;
};
